'use client';

import * as Sentry from '@sentry/nextjs';
import posthog from 'posthog-js';
import { useEffect } from 'react';

import { ANALYSIS_COOKIES_PROPERTY_NAME } from '@cineamo/ui/src/constants/cookie-constants';

import useCookieConsent from '@cineamo/ui/src/hooks/useCookieConsent';

import { IS_ANALYTICS_ENABLED } from '@cineamo/composables/src/providers/analytics-environment';

import type { BookingContext } from './booking-telemetry';
import { clearBookingContext, setBookingContext, setRecordingConsent } from './booking-telemetry-client';

/**
 * Starts the session recordings of a booking, and only while the visitor is on one (CIN-15436, § 25 TDDDG).
 * Both need the analysis consent, mask what the buyer types, and stop when the booking unmounts or consent is
 * withdrawn.
 *
 *  - Sentry: replay is not part of the SDK init (it is lazy, see `sentry.client.config`). It buffers and uploads
 *    only for an error-level event (`replaysOnErrorSampleRate` 1.0, session rate 0, `beforeErrorSampling` drops warnings), so it costs nothing on a good checkout.
 *  - PostHog: `disable_session_recording` stays true everywhere; the recorder starts here and nowhere else.
 *
 * Inputs are masked and iframes blocked, so what the buyer types (the buyer sheet) and the Stripe elements (a
 * cross-origin iframe either way) never reach a recording. Page text and images stay readable (CIN-15446): a replay
 * of asterisks shows nothing of what went wrong. The seat map is a canvas, recorded at a low frame rate and quality.
 */
function startRecordings(): () => void {
    // PostHog is initialized by a provider above, whose effect runs after this one's: wait one tick for it.
    let ph: ReturnType<typeof setTimeout> | undefined = setTimeout(() => {
        ph = undefined;
        if (!IS_ANALYTICS_ENABLED || !posthog.__loaded) return;
        posthog.set_config({
            session_recording: {
                maskAllInputs: true,
                maskTextSelector: null,
                blockSelector: 'iframe',
                recordCrossOriginIframes: false,
                // The client value wins over the project's setting, which stays off for every other page.
                captureCanvas: { recordCanvas: true, canvasFps: 2, canvasQuality: '0.2' },
                canvasCapture: { resolutionScale: 0.5 }
            }
        });
        posthog.startSessionRecording();
    }, 0);

    const replay = Sentry.getReplay();
    if (replay) {
        replay.startBuffering();
    } else if (Sentry.getClient()) {
        // Installed lazily: the client's sample rates (0 / 1.0) then start it in buffer mode.
        // First: the replay reads the canvas integration when it starts. The seat map's 2D canvas, about one frame a
        // second as low-quality WebP, uploaded only with an error replay.
        Sentry.addIntegration(Sentry.replayCanvasIntegration({ quality: 'low' }));
        Sentry.addIntegration(
            Sentry.replayIntegration({
                maskAllText: false,
                maskAllInputs: true,
                blockAllMedia: false,
                block: ['iframe'],
                // Warning-level reports (captureMessage, buyer-side noise) must not upload a replay: cost.
                beforeErrorSampling: (event) => event.level === 'error' || event.level === 'fatal'
            })
        );
    }

    return () => {
        if (ph !== undefined) clearTimeout(ph);
        if (IS_ANALYTICS_ENABLED && posthog.__loaded) posthog.stopSessionRecording();
        void Sentry.getReplay()?.stop();
    };
}

/**
 * Mounted by the CartProvider, the one component every booking flow (web, cinema websites, embedded) renders.
 * Puts the booking's ids on the Sentry scope for the visit, and runs the recordings under consent.
 */
export default function BookingObservability(context: BookingContext): null {
    const consentGranted = useCookieConsent(ANALYSIS_COOKIES_PROPERTY_NAME);
    const { cart_uuid, showing_id, cinema_id, client } = context;

    useEffect(() => {
        setBookingContext({ cart_uuid, showing_id, cinema_id, client });
    }, [cart_uuid, showing_id, cinema_id, client]);

    useEffect(() => clearBookingContext, []);

    // The replay ids go into the client context only while consent is given (see withClientContext).
    useEffect(() => {
        setRecordingConsent(consentGranted);
        return () => setRecordingConsent(false);
    }, [consentGranted]);

    useEffect(() => (consentGranted ? startRecordings() : undefined), [consentGranted]);

    return null;
}
